import {
  ApiError,
  HotelAvailabilityStatusType,
  HotelDetailViewModel,
  LoyaltyType,
  RoomVariationViewModel,
} from '@api/index';
import { AlertType } from '@components/views/hotel/detail/components/room-selection/components/Alert';

import findDiffDates from './findDiffDates';
import { replaceAll } from './replaceAll';

export const formatArrayToString = (param: string | string[]) =>
  Array.isArray(param) ? param[0] : param;

export const prepareQueryParams = (params) => {
  let queryParams = '';

  if (params?.in) {
    queryParams = `${queryParams}in=${params?.in}`;
  }

  if (params?.out) {
    queryParams = `${queryParams}&out=${params?.out}`;
  }

  if (params?.room) {
    queryParams = `${queryParams}&room=${params?.room}`;
  }

  if (params?.date && Array.isArray(params?.date)) {
    (params?.date as string[]).map(
      (date) => (queryParams = `${queryParams}&date=${date}`)
    );
  }

  if (params?.date && typeof params?.date === 'string') {
    queryParams = `${queryParams}&date=${params?.date}`;
  }

  return queryParams;
};

interface AvailableStatus {
  button: string;
  alertType?: AlertType;
}

export const getAvailableStatus = (status?: string): AvailableStatus => {
  switch (status) {
    case HotelAvailabilityStatusType.AVAILABLE:
      return {
        button: 'Odayı Seç',
      };
    case HotelAvailabilityStatusType.ASK_SELL_PLAN:
    case HotelAvailabilityStatusType.TOO_LATE:
    case HotelAvailabilityStatusType.NOT_IN_STOCK:
      return {
        button: 'Yeniden Ara',
        alertType: AlertType.SECONDARY,
      };
    case HotelAvailabilityStatusType.SHORT_ACCOMMODATION:
    case HotelAvailabilityStatusType.MIN_STAY_NOT_SATISFIED:
    case HotelAvailabilityStatusType.SHORT_ACCOMMODATION_AND_MIN_STAY_TO_ASK_SELL_FALSE:
      return {
        button: 'Tarih Seç',
        alertType: AlertType.PRIMARY,
      };
    case HotelAvailabilityStatusType.CLOSED_QUOTA:
    case HotelAvailabilityStatusType.N_A:
    case HotelAvailabilityStatusType.MAX_PEOPLE_EXCEEDED:
    case HotelAvailabilityStatusType.ACCOMMODATION_TOO_LONG:
    case HotelAvailabilityStatusType.RESTRICTED:
      return {
        button: 'Yeniden Ara',
        alertType: AlertType.PRIMARY,
      };

    default:
      return {
        button: 'Odayı Seç',
      };
  }
};

interface GetRoomInformation {
  adultNumber?: number;
  childNumber?: number;
  childDates?: string[];
  roomNumber?: number;
  dateDifference?: number;
  message: string;
}
export const parseRoomString = (roomString: string) => {
  if (!roomString) return [];

  const roomParts = roomString.split(',');

  return roomParts.map((part) => {
    const [adultInfo, ...rest] = part.split('_');

    return {
      adultCount: parseInt(adultInfo, 10),
      childrenList: rest.map((date) => ({ age: date })),
    };
  });
};

export const getRoomInformations = (
  roomInfo?: string,
  startDate?: string,
  endDate?: string
): GetRoomInformation => {
  let dateDifference;

  if (startDate && endDate) {
    const a = new Date(startDate);
    const b = new Date(endDate);
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    dateDifference = Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
  }
  const rooms = parseRoomString(roomInfo || '');
  const adultNumber = rooms.reduce((sum, room) => sum + room.adultCount, 0);
  const childDates = rooms.flatMap((room) =>
    room.childrenList.map((child) => child.age)
  );
  return {
    adultNumber: adultNumber,
    childNumber: childDates.length,
    dateDifference: dateDifference,
    roomNumber: rooms.length,
    message: `${adultNumber ? adultNumber + ' yetişkin' : ''} ${
      childDates.length ? childDates.length + ' çocuk' : ''
    } ${dateDifference ? dateDifference + ' gece' : ''}`,
  } as GetRoomInformation;
};
export const typeToName = (type?: string): string => {
  switch (type) {
    case LoyaltyType.CHIPPIN:
      return 'Chippin';
    case LoyaltyType.HOPI_BOOSTER:
      return 'Hopi Booster';
    case LoyaltyType.HOPI_KAZANIM:
      return 'Hopi Kazanım';
    case LoyaltyType.KOC_AILEM:
      return 'Koç Ailem';
    case LoyaltyType.PARO:
      return 'Paro';
    case LoyaltyType.SETUR_HEDIYE_PUAN:
      return 'Setur Hediye Puan';
    case LoyaltyType.TANI_HEDIYE_KART:
      return 'Tanı Hediye Kart';
    case LoyaltyType.TAV:
      return 'Tav';
    case LoyaltyType.TURK_TELEKOM:
      return 'Türk Telekom';
    case LoyaltyType.TURK_TELEKOM_PRIME:
      return 'Türk Telekom Prime';
    case LoyaltyType.WORLD_PUAN:
      return 'World Puan';
    case LoyaltyType.ZUBIZU:
      return 'Zubizu';

    default:
      return '';
  }
};

export const updateArrayWithoutMutating = (
  data: any[],
  newProperty: any,
  id: string | number
) => {
  const objectIndex = data.findIndex((obj) => obj.id === id);

  const updatedObj = { ...data[objectIndex], ...newProperty };

  const updatedData = [
    ...data.slice(0, objectIndex),
    updatedObj,
    ...data.slice(objectIndex + 1),
  ];

  return updatedData;
};

export const getPeopleInformations = (
  adultCount?: number,
  childCount?: number,
  nightCount?: number,
  roomCount?: number
) => {
  return `${roomCount ? `${roomCount} oda, ` : ''}${
    adultCount ? `${adultCount} yetişkin` : ''
  }${childCount ? `, ${childCount} çocuk` : ''}${
    nightCount ? `, ${nightCount} gece` : ''
  }`;
};

export const getLocations = (
  region?: string,
  city?: string,
  country?: string
) => {
  return `${region ? `${region}, ` : ''}${city ? `${city}/` : ''}${
    country ? country : ''
  }`;
};

export const getUrlParams = (search: string) =>
  `${search}?`
    .split('?')[1]
    .split('&')
    .reduce((params: Record<string, unknown>, pair: string) => {
      const [key, value] = `${pair}=`.split('=').map(decodeURIComponent);

      return key.length > 0 ? { ...params, [key]: value } : params;
    }, {});

export const removeStorageItems = (keys: string[]) =>
  keys.forEach((key) => localStorage.removeItem(key));

type HotelDetailChildAndVariationsType = {
  variations: { variation: RoomVariationViewModel; boardType: string }[];
  hasChildFreeStatus: boolean;
};

export const getHotelDetailChildAndVariationsInfo = (
  hotelDetail: HotelDetailViewModel
): HotelDetailChildAndVariationsType => {
  let minPrice = Number.MAX_SAFE_INTEGER;
  let maxPrice = 0;
  let variationWithMinPrice;
  let variationWithMaxPrice;
  let hasChildFreeStatus = false;
  hotelDetail.rooms?.forEach((room) =>
    room.details?.forEach((detail) =>
      detail.informations?.forEach((info) => {
        if (info.childWithoutPriceCount) {
          hasChildFreeStatus = true;
        }
        info.variations?.forEach((variation) => {
          const price = variation.totalSellingPrice;
          if (price !== undefined) {
            if (price > maxPrice) {
              maxPrice = price;
              variationWithMaxPrice = {
                variation,
                boardType: detail.roomInfo?.boardType,
              };
            }
            if (price < minPrice) {
              minPrice = price;
              variationWithMinPrice = {
                variation,
                boardType: detail.roomInfo?.boardType,
              };
            }
          }
        });
      })
    )
  );

  return {
    variations:
      variationWithMinPrice && variationWithMaxPrice
        ? [variationWithMinPrice, variationWithMaxPrice]
        : [],
    hasChildFreeStatus,
  };
};

export const getNightCount = (
  checkInDate: string | string[] | undefined,
  checkOutDate: string | string[] | undefined
) => {
  return checkInDate && checkOutDate
    ? findDiffDates(String(checkInDate), String(checkOutDate))
    : undefined;
};
export const getCurrencySymbol = (currencyCode = '') => {
  switch (currencyCode?.toLowerCase()) {
    case 'tl':
    case 'try':
    case '₺':
      return '₺';
    case 'usd':
    case '$':
      return '$';
    case 'eur':
    case '€':
      return '€';
    case 'gbp':
    case '£':
      return '£';
    default:
      return '₺';
  }
};

export const MIN_SEARCH_LENGTH = 2;

export const capitalizeWords = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getBaggage = (baggage?: string) => {
  const customBaggage = replaceAll(baggage, 'KG', '');
  if (!customBaggage || customBaggage === '0' || customBaggage === '0P')
    return '';

  return `${customBaggage} KG Bagaj`;
};
export const removeBrTagFromMarkdown = (content: string) => {
  if (!content) return '';

  return content.replace(/<br[^>"']*((("[^"]*")|('[^']*'))[^"'>]*)*>/g, '');
};

export const removeImgTagFromMarkdown = (content: string) => {
  if (!content) return '';

  return content.replace(/<img[^>"']*((("[^"]*")|('[^']*'))[^"'>]*)*>/g, '');
};

export const formatCurrency = (currencyCode = '') => {
  switch (currencyCode?.toLowerCase()) {
    case 'tl':
    case '₺':
    case 'try':
      return 'TL';
    case 'usd':
    case '$':
      return 'USD';
    case 'eur':
    case '€':
      return 'EUR';
    case 'gbp':
    case '£':
      return 'GBP';
    case 'puan':
      return 'Puan';
    default:
      return 'TL';
  }
};

export const is404RequestError = (error: ApiError) => {
  const errors = error?.body?.status === 404;
  return errors;
};

export const formatStringToArray = (key: string | string[] | undefined) => {
  if (!key) return undefined;

  if (Array.isArray(key)) {
    return key;
  }

  return key.split(',');
};

export const flattenSearchParams = (
  params: Record<string, string | string[] | undefined>
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const key in params) {
    const value = params[key];
    if (value !== undefined) {
      result[key] = Array.isArray(value) ? value[0] : value;
    }
  }

  return result;
};
